import { __rest } from '../../_virtual/_tslib.js';
import React from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styles from '../../node_modules/@flixbus/honeycomb/dist/scss/components/panel.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import * as reactUtils from '../../packages/react-utils/react-utils.js';
import '@flixbus/honeycomb-icons-react';
import { InnerRefPropType } from '../../packages/props-utils/prop-types.js';
import { useTabTrap } from '../../hooks/_use-tab-trap/use-tab-trap.js';
import { panelChildrenValidator } from './panel-children-validator.js';
import { PANEL_POSITIONS } from './types.js';
import { PanelContext } from './panel-context.js';
import { useDelayedHidden } from '../../hooks/use-delayed-hidden/use-delayed-hidden.js';
import { useLockBodyScroll } from '../../hooks/use-lock-body-scroll/use-lock-body-scroll.js';
import Overlay from '../overlay/overlay.js';
import { Injecss } from '../../packages/injecss/injecss.js';
import { usePortal } from '../../hooks/use-portal/use-portal.js';

const bemPanel = new HoneyBem('panel', styles);
function Panel(_a) {
  var {
      id,
      children,
      active = false,
      hasOverlay = false,
      onOverlayClick,
      extraClasses,
      position,
      fullSize = false,
      portalId,
      innerRef,
      targetRef,
      loading,
      disableBodyStyleModifications = false,
      disableFocusManagement = false
    } = _a,
    rest = __rest(_a, ["id", "children", "active", "hasOverlay", "onOverlayClick", "extraClasses", "position", "fullSize", "portalId", "innerRef", "targetRef", "loading", "disableBodyStyleModifications", "disableFocusManagement"]);
  const panelRef = innerRef !== null && innerRef !== void 0 ? innerRef : React.useRef(null);
  const container = portalId && usePortal(portalId);
  const [hiddenAttr, activeModifier] = useDelayedHidden(active);
  // Tab trap activates with the `activeModifier`, to be sure the `hidden` attribute is gone before
  // querying for the focusable elements
  const activeAndLoaded = activeModifier && !loading;
  const tabTrap = !disableFocusManagement && hasOverlay && typeof panelRef !== 'function' && typeof targetRef !== 'function' ? useTabTrap(activeAndLoaded, panelRef.current, targetRef === null || targetRef === void 0 ? void 0 : targetRef.current) : null;
  if (!disableBodyStyleModifications && hasOverlay) {
    useLockBodyScroll(!hiddenAttr);
  }
  const handleKeyDown = event => {
    var _a, _b;
    // Listen to ESC button press to close Panel with keyboard
    if (event.key === 'Escape' || event.key === 'Esc') {
      onOverlayClick === null || onOverlayClick === void 0 ? void 0 : onOverlayClick(event);
      event.stopPropagation();
    }
    (_a = tabTrap === null || tabTrap === void 0 ? void 0 : tabTrap.handleTabKeyDown) === null || _a === void 0 ? void 0 : _a.call(tabTrap, event);
    (_b = rest.onKeyDown) === null || _b === void 0 ? void 0 : _b.call(rest, event);
  };
  const getSection = sectionName => reactUtils.getChildrenByName(children, sectionName)[0];
  const panelTitleId = "".concat(id, "-title");
  const panelState = React.useMemo(() => ({
    titleId: panelTitleId,
    active: !hiddenAttr,
    loading
  }), [panelTitleId, hiddenAttr, loading]);
  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
  const panel = /*#__PURE__*/React.createElement("div", Object.assign({
    ref: panelRef,
    id: id,
    className: bemPanel.b({
      active: activeModifier,
      left: position === 'left',
      bottom: position === 'bottom',
      center: position === 'center',
      full: fullSize
    }, extraClasses),
    role: "dialog",
    "aria-labelledby": panelTitleId,
    hidden: hiddenAttr
  }, hasOverlay ? {
    'aria-modal': 'true'
  } : {}, loading !== undefined ? {
    'aria-live': 'polite',
    'aria-busy': loading
  } : {}, rest, {
    onKeyDown: handleKeyDown
  }), /*#__PURE__*/React.createElement(PanelContext.Provider, {
    value: panelState
  }, /*#__PURE__*/React.createElement("div", {
    className: bemPanel.e('body')
  }, getSection('PanelHeader'), getSection('PanelContent'), getSection('PanelFooter'))), hasOverlay && /*#__PURE__*/React.createElement(Overlay, {
    extraClasses: bemPanel.e('overlay'),
    onClick: onOverlayClick
  }));
  return container ? /*#__PURE__*/createPortal(panel, container) : panel;
}
Panel.displayName = 'Panel';
Panel.propTypes = {
  id: PropTypes.string.isRequired,
  children: panelChildrenValidator(),
  active: PropTypes.bool,
  hasOverlay: PropTypes.bool,
  onOverlayClick: PropTypes.func,
  extraClasses: PropTypes.string,
  position: PropTypes.oneOf(PANEL_POSITIONS),
  fullSize: PropTypes.bool,
  portalId: PropTypes.string,
  innerRef: InnerRefPropType,
  targetRef: InnerRefPropType,
  disableBodyStyleModifications: PropTypes.bool,
  disableFocusManagement: PropTypes.bool,
  loading: PropTypes.bool
};
var panel = Injecss(Panel, styles);

export { Panel, panel as default };
